import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import Home from './pages/Home';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Lazy load non-critical pages for better mobile performance
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const StaffLogin = lazy(() => import('./pages/StaffLogin'));
const Resources = lazy(() => import('./pages/Resources'));
const ContractorResources = lazy(() => import('./pages/ContractorResources'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const GuideTemplate = lazy(() => import('./pages/guides/GuideTemplate'));
const Partners = lazy(() => import('./pages/Partners'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Founding = lazy(() => import('./pages/Founding'));

// GEO (Generative Engine Optimization) Pages
const Authority = lazy(() => import('./pages/Authority'));
const FAQ = lazy(() => import('./pages/FAQ'));
const WhyUs = lazy(() => import('./pages/WhyUs'));

// Location SEO Pages
const WestVirginia = lazy(() => import('./pages/locations/WestVirginia'));
const ReadingPA = lazy(() => import('./pages/locations/ReadingPA'));
const MohntonPA = lazy(() => import('./pages/locations/MohntonPA'));
const SanFranciscoCA = lazy(() => import('./pages/locations/SanFranciscoCA'));
const OaklandCA = lazy(() => import('./pages/locations/OaklandCA'));

// Contractor Recruitment
const ContractorJoin = lazy(() => import('./pages/ContractorJoin'));
const ForGeneralContractors = lazy(() => import('./pages/ForGeneralContractors'));
const Pros = lazy(() => import('./pages/Pros'));

// Business / Property Manager / HOA
const ForBusinesses = lazy(() => import('./pages/ForBusinesses'));

// 404
const NotFound = lazy(() => import('./pages/NotFound'));

// Simple loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <div>Loading...</div>
  </div>
);

// Redirect component for external links
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return <div className="loading-screen">Redirecting...</div>;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <a href="#main-content" className="skip-to-content">Skip to main content</a>
        <Navbar />
        <div id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<StaffLogin />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<GuideTemplate />} />
            <Route path="/contractor-resources" element={<ContractorResources />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/founding" element={<Founding />} />
            {/* GEO Pages */}
            <Route path="/authority" element={<Authority />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/why-us" element={<WhyUs />} />
            {/* Location SEO Pages */}
            <Route path="/locations/west-virginia" element={<WestVirginia />} />
            <Route path="/locations/reading-pennsylvania" element={<ReadingPA />} />
            <Route path="/locations/mohnton-pennsylvania" element={<MohntonPA />} />
            <Route path="/locations/san-francisco-california" element={<SanFranciscoCA />} />
            <Route path="/locations/oakland-california" element={<OaklandCA />} />
            {/* Contractor Recruitment */}
            <Route path="/contractors/join" element={<ContractorJoin />} />
            <Route path="/for-general-contractors" element={<ForGeneralContractors />} />
            <Route path="/pros" element={<Pros />} />
            <Route path="/for-businesses" element={<ForBusinesses />} />
            <Route path="/signup" element={<ExternalRedirect to="https://app.realtimerepairs.com/register" />} />
            {/* Redirect legal pages to dashboard */}
            <Route path="/terms" element={<ExternalRedirect to="https://app.realtimerepairs.com/terms-of-service" />} />
            <Route path="/privacy" element={<ExternalRedirect to="https://app.realtimerepairs.com/privacy-policy" />} />
            {/* 404 Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
